/**
 * FoodPulseContributorsIndex — public roster of expert contributors.
 *
 * URL: /food-pulse/contributors
 *
 * Renders every ``live`` contributor with role chip + credentials.
 * Links each card into the full profile page.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, ArrowRight, Loader2, ShieldCheck, Utensils, Sprout, BookOpen, BadgeCheck,
} from "lucide-react";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import FoodPulseTopNav from "@/components/FoodPulseTopNav";
import FoodPulseJourneyNav from "@/components/FoodPulseJourneyNav";

const ROLE_META = {
  "dietitian":               { label: "Registered Dietitian",     Icon: Utensils,   tint: "bg-emerald-50 text-emerald-800 ring-emerald-200" },
  "food-safety-specialist":  { label: "Food Safety Specialist",   Icon: ShieldCheck, tint: "bg-sky-50 text-sky-800 ring-sky-200" },
  "farmer":                  { label: "Working Farmer",           Icon: Sprout,     tint: "bg-amber-50 text-amber-800 ring-amber-200" },
  "editor":                  { label: "Editorial Team",           Icon: BookOpen,   tint: "bg-slate-100 text-slate-700 ring-slate-200" },
};

export default function FoodPulseContributorsIndex() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await http.get("/food-pulse/contributors");
        if (!cancelled) setItems(r.data?.items || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="fp-contributors-index">
      <SeoHead
        section="food-pulse"
        title="Expert contributors — FoodPulse"
        description="Named dietitians, food-safety specialists, farmers, and editors who review every FoodPulse guide."
      />
      <FoodPulseTopNav />
      <FoodPulseJourneyNav />

      <section className="mx-auto max-w-4xl px-4 py-10">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-emerald-700">
          <Users className="h-3.5 w-3.5" /> Expert contributors
        </div>
        <h1 className="mt-2 font-serif text-3xl text-slate-900 sm:text-4xl">
          Real people. Real credentials. Real corrections.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-slate-600 sm:text-base">
          Every cornerstone guide on FoodPulse carries a byline from a named
          dietitian, food-safety specialist, farmer, or staff editor — with
          a public corrections log so you can see what changed and when.
        </p>

        {loading ? (
          <div className="mt-8 flex items-center gap-2 text-sm text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading contributors…
          </div>
        ) : (
          <ul
            className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2"
            data-testid="fp-contributors-list"
          >
            {items.map((c) => {
              const meta = ROLE_META[c.role] || ROLE_META.editor;
              const { Icon } = meta;
              return (
                <li key={c.slug} data-testid={`fp-contributor-card-${c.slug}`}>
                  <Link
                    to={`/food-pulse/contributors/${c.slug}`}
                    className="group flex h-full items-start gap-3 rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-emerald-300 hover:shadow-md"
                  >
                    <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full ring-1 ${meta.tint}`}>
                      {c.headshot_url ? (
                        <img
                          src={c.headshot_url}
                          alt={c.name}
                          className="h-14 w-14 rounded-full object-cover"
                        />
                      ) : (
                        <Icon className="h-6 w-6" />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-slate-500">
                        <BadgeCheck className="h-3 w-3" /> {meta.label}
                      </div>
                      <div className="mt-0.5 font-serif text-lg text-slate-900 group-hover:text-emerald-800">
                        {c.name}
                      </div>
                      <div className="mt-1 line-clamp-2 text-xs text-slate-600">
                        {c.credentials}
                      </div>
                      <div className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700">
                        View profile
                        <ArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
                      </div>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
